import { type FC, useCallback, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { useForm } from 'react-final-form';
import type { CampaignFormValues } from '../campaign.types';
import { tagUniqueCombinations } from './MVT.util';
import { MVTCombinations } from './MVTCombinations';

type MVTApprovalModalProps = {
  onHide: () => void;
  onCancel: () => void;
  onApprove: () => void;
  hasPreviews: boolean;
};

export const MVTApprovalModal: FC<MVTApprovalModalProps> = ({ onHide, onCancel, onApprove, hasPreviews }) => {
  const [approved, setApproved] = useState(false);
  const form = useForm<CampaignFormValues>();
  const combinations = form.getState().values.campaign.mvtExperimentInfo?.combinations;
  const dimensions = form.getState().values.campaign.mvtExperimentInfo?.dimensions;
  const includedCombinations = combinations?.filter((combination) => !combination.isExcluded);
  const uniqueTaggedCombinations = includedCombinations && dimensions ? tagUniqueCombinations(includedCombinations, dimensions) : undefined;
  const uniqueCombinations = uniqueTaggedCombinations?.filter((combination) => combination.unique);
  const uniqueInverseCombinations = uniqueTaggedCombinations?.filter((combination) => !combination.unique);
  const totalUniqueCombinations = uniqueCombinations?.length ?? 0;
  const totalUniqueInverseCombinations = uniqueInverseCombinations?.length ?? 0;

  const handleOnPreview = useCallback(
    (totalPreviewed?: number) => {
      setApproved(totalPreviewed === totalUniqueCombinations);
    },
    [
      totalUniqueCombinations,
    ],
  );

  return (
    <Modal centered onHide={onHide} scrollable show size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Approve MVT Combinations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="primary">You must preview each combination directly below before approving</Alert>
        <MVTCombinations
          combinations={uniqueCombinations}
          hasPreviews={hasPreviews}
          hideAction
          onPreview={handleOnPreview}
          subtitle="Preview required"
          total={totalUniqueCombinations}
        />
        {totalUniqueInverseCombinations > 0 && (
          <>
            <Alert variant="primary">The following combinations will also be created</Alert>
            <MVTCombinations
              combinations={uniqueInverseCombinations}
              hasPreviews={hasPreviews}
              hideAction
              subtitle="Preview optional"
              total={totalUniqueInverseCombinations}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} variant="link">
          Cancel
        </Button>
        <Button disabled={!approved} onClick={onApprove} variant="primary">
          Approve
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
